import type { RequestInit } from '@vercel/fetch'
import { GraphQLError, graphqlFetch } from '@pylot-data/graphqlFetch'
import {
    SWRInfiniteConfiguration,
    SWRInfiniteResponse,
    useSWRInfinite
} from 'swr'
import { useRouter } from 'next/router'

export type SwrUsePaginatedQueryOutput<OutputInterface> = SWRInfiniteResponse<
    OutputInterface,
    GraphQLError
>

export type FilterAndSortVariables = {
    currentPage?: number
    pageSize?: number
    filter: {
        [filter: string]: {
            [type: string]: string
        }
    }
}

export const usePaginatedQuery = <
    VariablesInterface,
    OutputInterface,
    OutputError = GraphQLError
>(
    query: string | undefined | null,
    variablesRaw?: VariablesInterface | null,
    sizeVariableName?: keyof VariablesInterface | null,
    swrOptions?: SWRInfiniteConfiguration,
    fetchOptions?: RequestInit
): SWRInfiniteResponse<OutputInterface, OutputError> => {
    const { locale } = useRouter()

    if (variablesRaw) {
        if (!sizeVariableName) {
            throw new Error(
                'UsePaginatedQuery: Variable name for size value is not indicated'
            )
        }
        if (!(sizeVariableName in variablesRaw)) {
            throw new Error(
                `UsePaginatedQuery: Property '${sizeVariableName}' is not defined inside the object with variables`
            )
        }
    }

    function getVars(size: number) {
        if (!query) return null

        if (!variablesRaw) {
            return [query]
        }

        const variables: VariablesInterface = {
            ...variablesRaw!
        }

        if (sizeVariableName) {
            ;((variables[sizeVariableName] as unknown) as number) = size + 1
        }

        const sortedVariables = Object.entries(variables).sort(
            ([key1], [key2]) => {
                if (key1 < key2) return -1
                else if (key1 > key2) return 1
                else return 0
            }
        )

        const queryKey = [
            query,
            ...sortedVariables!.map((variableEntry) =>
                JSON.stringify(variableEntry[1])
            )
        ]

        return queryKey ?? null
    }
    const getVariablesWithFilter = (search: {
        filter: { [p: string]: { [p: string]: string } }
        searchCriteria?: {
            attribute_code: string
            filter_action: string
            filter_value: string
        }[]
    }) => {
        const filter = {} as {
            [filter: string]: {
                [type: string]: string
            }
        }
        if (search?.searchCriteria) {
            search.searchCriteria.forEach((item) => {
                const { attribute_code, filter_action, filter_value } = item

                if (!filter[attribute_code]) {
                    filter[attribute_code] = {}
                }
                filter[attribute_code][filter_action] = filter_value
            })
            delete search.searchCriteria
        }
        search.filter = filter

        return search
    }

    return useSWRInfinite(
        getVars,
        (query, ...variableValues) => {
            const sortedVariables = variablesRaw
                ? Object.entries(variablesRaw).sort(([key1], [key2]) => {
                      if (key1 < key2) return -1
                      else if (key1 > key2) return 1
                      else return 0
                  })
                : null
            const res = graphqlFetch<FilterAndSortVariables, OutputInterface>({
                query: query!,
                variables: getVariablesWithFilter(
                    variableValues
                        ? variableValues.reduce((obj, val, i) => {
                              obj[sortedVariables![i][0]!] = JSON.parse(val)
                              return obj
                          }, {})
                        : {}
                ),
                fetchOptions,
                locale
            })
            return res
        },
        swrOptions
    )
}
